<template>
  <div class="invoice-monitor">
    <!-- 筛选栏 -->
    <div class="filter-section">
      <div class="filter-grid">
        <!-- Row 1: 5 filter fields -->
        <div class="filter-item">
          <label class="filter-label">日期</label>
          <el-date-picker
            style="width:100%;"
            format="YYYY-MM"
            value-format="YYYYMM"
            v-model="filters.month"
            type="month"
            placeholder="选择月"
            class="filter-input"
          />
        </div>
        <div class="filter-item">
          <label class="filter-label">地区</label>
          <el-select v-model="filters.region" style="width:100%" placeholder="请选择" class="filter-input">
            <el-option label="全部" value="all" />
            <el-option label="global" value="global" />
            <el-option label="cn" value="cn" />
          </el-select>
        </div>
        <div class="filter-item">
          <label class="filter-label">Payers</label>
          <el-select v-model="filters.payer_id" clearable filterable style="width:100%" placeholder="请选择" class="filter-input">
            <el-option v-for="item in payersList" :key="item.payer_id" :label="item.name+'('+item.payer_id+')'" :value="item.payer_id" />
          </el-select>
        </div>
        <div class="filter-item">
          <label class="filter-label">销售</label>
          <el-select clearable filterable v-model="filters.sales" placeholder="请选择" style="width:100%" :disabled="!isAdmin" class="filter-input">
            <el-option v-for="s in salesList" :key="s.id" :label="s.name" :value="s.name" />
          </el-select>
        </div>
        <div class="filter-item">
          <label class="filter-label">合作伙伴</label>
          <el-select clearable filterable v-model="filters.agent_id" placeholder="请选择" style="width:100%" class="filter-input">
            <el-option v-for="item in agentList" :key="item.agent_id + '_' + item.company" :label="item.company + ' (' + item.agent_id + ')'" :value="item.agent_id" />
          </el-select>
        </div>
        <!-- Row 2: LinkId + action buttons -->
        <div class="filter-actions">
          <div class="filter-item" style="flex: 0 0 160px;">
            <label class="filter-label">LinkId</label>
            <el-select v-model="filters.link_id" clearable filterable style="width:100%" placeholder="请选择" class="filter-input"
              :filter-method="linkFilterMethod">
              <el-option v-for="item in displayedLinksList" :key="item.link_id"
                :label="(item.customer_name || item.name || item.link_id) + '（' + item.link_id + '）'"
                :value="String(item.link_id)" />
            </el-select>
          </div>
          <div class="filter-item" style="flex: 0 0 150px;">
            <label class="filter-label">CUR完整性</label>
            <el-select v-model="filters.cur_integrity" style="width:100%" placeholder="全部" class="filter-input">
              <el-option label="全部" value="all" />
              <el-option label="✅ 完整" value="complete" />
              <el-option label="⚠️ 部分缺失" value="partial" />
              <el-option label="❌ 完全缺失" value="missing" />
            </el-select>
          </div>
          <el-button @click="resetFilters" class="action-btn">
            <el-icon><Refresh /></el-icon>
            重置
          </el-button>
          <el-button @click="showUploadDialog" class="action-btn">
            <el-icon><Upload /></el-icon>
            上传Invoice
          </el-button>
          <el-button @click="startDownloadDirect" class="action-btn action-btn--dark" :loading="downloading">
            <el-icon><Download /></el-icon>
            同步Global Invoice
          </el-button>
          <el-button @click="generateAllReconciliation" class="action-btn">
            <el-icon><Document /></el-icon>
            生成全部对账单
          </el-button>
          <el-checkbox v-model="filterZeroData" class="filter-checkbox">过滤0费用数据</el-checkbox>
          <el-button @click="queryData" class="action-btn action-btn--primary" style="margin-left: auto;">
            <el-icon><Search /></el-icon>
            查询
          </el-button>
        </div>
      </div>
    </div>

    <!-- 监控数据表格 -->
    <div class="monitor-table">
      <el-table
        :key="tableKey"
        :data="monitorData"
        v-loading="loading"
        border
        height="500"
        style="width: 100%"
        :tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
        row-key="id"
        lazy
        :load="loadChildren"
        ref="monitorTableRef"
      >
        <el-table-column prop="name" label="名称" width="300" />
        <el-table-column label="Excel账单" width="120">
          <template #default="{ row }">
            <span v-if="row.type === 'link' && row.excel_progress">
              {{ row.excel_progress.status }} {{ row.excel_progress.generated }}/{{ row.excel_progress.total }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="PDF账单" width="120">
          <template #default="{ row }">
            <span v-if="row.type === 'link' && row.pdf_progress">
              {{ row.pdf_progress.status }} {{ row.pdf_progress.generated }}/{{ row.pdf_progress.total }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="伙伴对账单" width="120">
          <template #default="{ row }">
            <span v-if="row.type === 'link' && row.detail_agent_progress">
              {{ row.detail_agent_progress.status }} {{ row.detail_agent_progress.generated }}/{{ row.detail_agent_progress.total }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="客户对账单" width="120">
          <template #default="{ row }">
            <span v-if="row.type === 'link' && row.detail_client_progress">
              {{ row.detail_client_progress.status }} {{ row.detail_client_progress.generated }}/{{ row.detail_client_progress.total }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="整合表" width="120">
          <template #default="{ row }">
            <span v-if="row.type === 'link' && row.integration_progress">
              {{ row.integration_progress.status }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="CUR完整性" width="160">
          <template #default="{ row }">
            <span v-if="row.type === 'link' && row.cur_integrity"
                  :style="{ color: curIntegrityColor(row.cur_integrity.code), 'font-size': '12px' }">
              {{ curIntegrityIcon(row.cur_integrity.code) }} {{ row.cur_integrity.label }}
              <template v-if="row.cur_integrity.code !== 'complete' && row.cur_integrity.diff > 0">
                <br/>缺 {{ row.cur_integrity.diff }}
                <span v-if="row.cur_integrity.inv_gross > 0">
                  ({{ ((row.cur_integrity.diff / row.cur_integrity.inv_gross) * 100).toFixed(1) }}%)
                </span>
              </template>
            </span>
          </template>
        </el-table-column>
        <el-table-column label="Invoice下载" width="120">
          <template #default="{ row }">
            <span v-if="row.type === 'payer'">
              <span v-if="row.invoice_download_progress">
                {{ row.invoice_download_progress.downloaded }}/{{ row.invoice_download_progress.total }}
              </span>
              <span v-else style="color: #909399;">-</span>
            </span>
          </template>
        </el-table-column>
        <el-table-column label="失败原因" width="200">
          <template #default="{ row }">
            <div v-if="row.type === 'link'" style="font-size: 12px; color: #f56c6c;">
              <div v-if="row.excel_progress?.error">Excel: {{ row.excel_progress.error }}</div>
              <div v-if="row.pdf_progress?.error">PDF: {{ row.pdf_progress.error }}</div>
              <div v-if="row.detail_agent_progress?.error">伙伴对账单: {{ row.detail_agent_progress.error }}</div>
              <div v-if="row.detail_client_progress?.error">客户对账单: {{ row.detail_client_progress.error }}</div>
              <div v-if="row.integration_progress?.error">整合表: {{ row.integration_progress.error }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" v-if="false">
          <template #default="{ row }">
            <el-button
              v-if="row.type === 'link'"
              type="primary"
              size="small"
              @click="regenerate(row)"
              style="display: none;"
            >
              重新生成
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 上传Invoice弹窗 -->
    <el-dialog v-model="uploadDialogVisible" title="上传Invoice" width="800px" @close="handleUploadDialogClose">
      <InvoiceUploadContent ref="uploadContentRef" @success="handleUploadSuccess" />
    </el-dialog>

    <!-- 自定义账单配置弹窗 -->
    <el-dialog v-model="configDialogVisible" title="自定义账单配置" width="1000px">
      <BillConfigContent @success="handleConfigSuccess" />
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed, watch, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { UploadFilled, Setting, Search, Document, Refresh, Upload, Download } from '@element-plus/icons-vue'
import InvoiceUploadContent from './InvoiceUploadContent.vue'
import BillConfigContent from './BillConfigContent.vue'
import { useBillDataStore } from '@/store/billData'
import { downloadBillDetail } from '@/api/api'

export default {
  name: 'InvoiceMonitor',
  components: {
    UploadFilled,
    Setting,
    Search,
    Document,
    Refresh,
    Upload,
    Download,
    InvoiceUploadContent,
    BillConfigContent
  },
  setup() {
    const loading = ref(false)
    const uploadDialogVisible = ref(false)
    const configDialogVisible = ref(false)
    const uploadContentRef = ref(null)
    const billDataStore = useBillDataStore()
    const filterZeroData = ref(true)  // 默认勾选：过滤0费用数据
    
    const filters = reactive({
      month: '',
      sales: '',
      agent_id: '',
      payer_id: '',
      region: 'all',
      link_id: '',
      cur_integrity: 'all'
    })
    
    // 监听级联清空
    watch(() => filters.sales, () => {
      filters.agent_id = ''
      filters.payer_id = ''
      filters.link_id = ''
    })
    watch(() => filters.agent_id, () => {
      filters.payer_id = ''
      filters.link_id = ''
    })
    watch(() => filters.payer_id, () => {
      filters.link_id = ''
    })
    
    const salesList = computed(() => billDataStore.sales.filter(s => s.name !== 'test'))
    const agentList = computed(() => {
      if (!filters.sales) return []
      return billDataStore.agents.filter(a => {
        if (!a.sales) return false
        // 支持逗号分隔的多个sales（例如：'姚菲菲,李博扬'）
        const salesList = a.sales.split(',').map(s => s.trim())
        return salesList.includes(filters.sales)
      })
    })
    const payersList = computed(() => {
      if (!filters.agent_id) return billDataStore.payers
      // 查找所有匹配的agent记录（可能有多条）
      const matchingAgents = billDataStore.agents.filter(a => a.agent_id === filters.agent_id)
      if (matchingAgents.length === 0) return []
      
      // 收集所有匹配agent的payer_id
      const allPayerIds = new Set()
      matchingAgents.forEach(agent => {
        if (agent.payers) {
          const payerIds = agent.payers.split(',').map(id => id.trim()).filter(id => id)
          payerIds.forEach(id => allPayerIds.add(id))
        }
      })
      
      return billDataStore.payers.filter(p => allPayerIds.has(p.payer_id))
    })
    const linksList = computed(() => {
      let filtered = billDataStore.links
      if (filters.agent_id) filtered = filtered.filter(l => l.agent_id === filters.agent_id)
      if (filters.payer_id) filtered = filtered.filter(l => l.payer_id === filters.payer_id)
      return filtered
    })
    const displayedLinksList = ref([])
    
    // 初始化displayedLinksList
    watch(linksList, (val) => {
      displayedLinksList.value = val.slice(0, 50)
    }, { immediate: true })
    
    const linkFilterMethod = (query) => {
      if (!query) {
        displayedLinksList.value = linksList.value.slice(0, 50)
        return
      }
      const q = query.trim()
      displayedLinksList.value = linksList.value.filter(item => {
        return String(item.link_id).includes(q) ||
               (item.customer_name && item.customer_name.includes(q)) ||
               (item.name && item.name.includes(q))
      }).slice(0, 50)
    }
    
    const monitorData = ref([])
    const monitorTableRef = ref(null)
    const tableKey = ref(0)
    const childrenMap = ref({})  // id -> children array, for lazy loading
    
    const isAdmin = computed(() => {
      return localStorage.getItem('curRole') === 'admin'
    })
    
    // 初始化
    onMounted(async () => {
      if (!billDataStore.loaded) {
        await billDataStore.loadData()
      }
      initFilters()
      
      // 从localStorage加载过滤0费用数据选项
      const savedFilterZero = localStorage.getItem('filterZeroData')
      if (savedFilterZero !== null) {
        filterZeroData.value = savedFilterZero === 'true'
      }
    })
    
    // 监听filterZeroData变化，保存到localStorage
    watch(filterZeroData, (newVal) => {
      localStorage.setItem('filterZeroData', String(newVal))
    })
    
    // 初始化筛选条件
    const initFilters = () => {
      const now = new Date()
      const currentYear = now.getFullYear()
      const currentMonth = now.getMonth() + 1 // 1-12
      
      let lastYear = currentYear
      let lastMonth = currentMonth - 1
      
      if (lastMonth === 0) {
        lastYear = currentYear - 1
        lastMonth = 12
      }
      
      filters.month = lastYear + String(lastMonth).padStart(2, '0')
      
      if (!isAdmin.value) {
        filters.sales = localStorage.getItem('account') || ''
      }
    }
    
    // 查询数据
    const queryData = async () => {
      console.log('🔍 [queryData] 被调用，调用栈:', new Error().stack)
      
      if (!filters.month || !filters.sales) {
        ElMessage.warning('请选择月份和销售')
        return
      }
      
      loading.value = true
      try {
        const params = new URLSearchParams({
          month: filters.month,
          sales: filters.sales
        })
        
        if (filters.agent_id) params.append('agent_id', filters.agent_id)
        if (filters.payer_id) params.append('payer_id', filters.payer_id)
        if (filters.region) params.append('region', filters.region)
        if (filters.link_id) params.append('link_id', filters.link_id)
        if (filters.cur_integrity && filters.cur_integrity !== 'all') params.append('cur_integrity', filters.cur_integrity)
        
        console.log('📡 [queryData] Request params:', params.toString(), 'filters.payer_id=', filters.payer_id, 'filters.agent_id=', filters.agent_id)
        
        const response = await fetch(`/api/bill/invoice-monitor/?${params}`)
        const data = await response.json()
        
        if (data.code === 200) {
          const isFirstQuery = monitorData.value.length === 0
          const oldChildrenMap = { ...childrenMap.value }
          monitorData.value = buildTreeData(data.data)
          
          if (isFirstQuery) {
            // 首次查询：重建表格，自动展开 agent 层
            tableKey.value++
            nextTick(() => {
              const table = monitorTableRef.value
              if (table) {
                monitorData.value.forEach(agent => {
                  if (agent.hasChildren) {
                    table.toggleRowExpansion(agent, true)
                  }
                })
              }
            })
          } else {
            // 后续查询：原地更新 el-table 的 lazy 缓存（treeData / lazyTreeNodeMap）
            nextTick(() => {
              const table = monitorTableRef.value
              if (!table) return
              const store = table.store
              // el-table 内部用 states.lazyTreeNodeMap 缓存 lazy 加载的子节点
              const lazyMap = store.states.lazyTreeNodeMap.value || store.states.lazyTreeNodeMap
              if (lazyMap) {
                for (const nodeId of Object.keys(lazyMap)) {
                  if (childrenMap.value[nodeId]) {
                    // 用新数据替换缓存中的子节点
                    lazyMap[nodeId] = childrenMap.value[nodeId]
                  }
                }
              }
            })
          }
          ElMessage.success('查询成功')
        } else {
          ElMessage.error(data.msg || '查询失败')
        }
      } catch (error) {
        console.error('查询失败:', error)
        ElMessage.error('查询失败，请重试')
      } finally {
        loading.value = false
      }
    }
    
    // 构建树形数据（lazy模式：只返回顶层节点，children存入childrenMap）
    const buildTreeData = (data) => {
      const treeData = []
      const cMap = {}
      
      data.agents.forEach((agent, agentIndex) => {
        const agentId = `agent_${agentIndex}`
        const agentNode = {
          id: agentId,
          name: `${agent.company} (${agent.agent_id})`,
          type: 'agent',
          hasChildren: agent.payers.length > 0
        }
        
        const payerNodes = []
        agent.payers.forEach((payer, payerIndex) => {
          const payerId = `payer_${agentIndex}_${payerIndex}`
          const payerNode = {
            id: payerId,
            name: `${payer.payer_name}: ${payer.payer_id}`,
            type: 'payer',
            payer_id: payer.payer_id,
            invoice_download_progress: null,
            hasChildren: payer.invoices.length > 0
          }
          
          // 保留之前的下载进度
          const existingPayer = findExistingPayer(payer.payer_id)
          if (existingPayer && existingPayer.invoice_download_progress) {
            payerNode.invoice_download_progress = existingPayer.invoice_download_progress
          }
          
          const invoiceNodes = []
          payer.invoices.forEach((invoice, invoiceIndex) => {
            const invoiceId = `invoice_${agentIndex}_${payerIndex}_${invoiceIndex}`
            const invoiceNode = {
              id: invoiceId,
              name: `Invoice: ${invoice.invoice_id}`,
              type: 'invoice',
              hasChildren: invoice.links.length > 0
            }
            
            const linkNodes = invoice.links.map((link, linkIndex) => ({
              id: `link_${agentIndex}_${payerIndex}_${invoiceIndex}_${linkIndex}`,
              name: `Link: ${link.link_id} (${link.region})`,
              type: 'link',
              link_id: link.link_id,
              payer_id: payer.payer_id,
              region: link.region,
              excel_progress: link.excel_bill_progress,
              pdf_progress: link.pdf_bill_progress,
              detail_agent_progress: link.detail_agent_bill_progress,
              detail_client_progress: link.detail_client_bill_progress,
              integration_progress: link.integration_table_progress,
              cur_integrity: link.cur_integrity
            }))
            
            cMap[invoiceId] = linkNodes
            invoiceNodes.push(invoiceNode)
          })
          
          cMap[payerId] = invoiceNodes
          payerNodes.push(payerNode)
        })
        
        cMap[agentId] = payerNodes
        treeData.push(agentNode)
      })
      
      childrenMap.value = cMap
      return treeData
    }
    
    // 查找现有的Payer节点（用于保留下载进度）
    const findExistingPayer = (payerId) => {
      for (const agent of monitorData.value) {
        const agentChildren = childrenMap.value[agent.id]
        if (agentChildren) {
          for (const payer of agentChildren) {
            if (payer.payer_id === payerId) {
              return payer
            }
          }
        }
      }
      return null
    }
    
    // lazy load回调
    const loadChildren = (row, treeNode, resolve) => {
      const children = childrenMap.value[row.id] || []
      resolve(children)
    }
    
    // 重置筛选条件
    const resetFilters = () => {
      initFilters()
      filters.agent_id = ''
      filters.payer_id = ''
      filters.region = ''
      filters.link_id = ''
      filters.cur_integrity = 'all'
      monitorData.value = []
      childrenMap.value = {}
      tableKey.value++
    }
    
    // 下载相关变量（在setup顶层定义，避免被重置）
    const downloading = ref(false)
    const downloadTaskId = ref('')
    let progressTimer = null
    let hasQueriedOnce = false  // 在setup顶层定义，不会被重置
    
    // 直接开始下载（使用筛选栏的条件）
    const startDownloadDirect = async () => {
      if (!filters.month) {
        ElMessage.warning('请选择月份')
        return
      }
      
      // 防止重复点击
      if (downloading.value) {
        ElMessage.warning('下载任务进行中，请稍候')
        return
      }
      
      downloading.value = true
      try {
        const downloadParams = {
          month: filters.month,
          region: filters.region === 'all' ? '' : filters.region,
          sales: filters.sales,
          agent_id: filters.agent_id,
          payer_id: filters.payer_id,
          filter_zero: filterZeroData.value ? '1' : '0'  // 添加过滤参数
        }
        
        console.log('[Invoice下载] 发送POST请求:', downloadParams)
        
        const response = await fetch('/api/bill/invoice/download/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(downloadParams)
        })
        const data = await response.json()
        
        console.log('[Invoice下载] POST响应:', data)
        
        if (data.code === 200) {
          downloadTaskId.value = data.data.task_id
          ElMessage.success(`下载任务已启动，共${data.data.total_payers}个Payer`)
          // 开始轮询进度
          pollDownloadProgress(data.data.task_id)
        } else {
          ElMessage.error(data.msg || '启动下载失败')
          downloading.value = false
        }
      } catch (error) {
        console.error('[Invoice下载] 启动失败:', error)
        ElMessage.error('启动下载失败，请重试')
        downloading.value = false
      }
    }
    
    // 开始下载（旧版本，保留兼容）
    const startDownload = async () => {
      // 此函数已废弃，使用startDownloadDirect代替
      await startDownloadDirect()
    }
    
    // 轮询下载进度
    const pollDownloadProgress = (taskId) => {
      // 清除之前的定时器
      if (progressTimer) {
        clearInterval(progressTimer)
        progressTimer = null
      }
      
      // 重置查询标记（只在启动新任务时重置）
      hasQueriedOnce = false
      
      console.log('[Invoice下载] 开始轮询进度, task_id:', taskId)
      console.log('[Invoice下载] hasQueriedOnce 初始化为:', hasQueriedOnce)
      
      progressTimer = setInterval(async () => {
        try {
          const response = await fetch(`/api/bill/invoice/download/?task_id=${taskId}`)
          const data = await response.json()
          
          console.log('[Invoice下载] 轮询响应 - status:', data.data?.status, 'hasQueriedOnce:', hasQueriedOnce)
          
          if (data.code === 200) {
            const task = data.data
            // 更新表格中的下载进度（不触发查询）
            updateDownloadProgress(task)
            
            // 只在第一次进入 generating_bills 状态时查询一次
            if (task.status === 'generating_bills' && !hasQueriedOnce) {
              console.log('✅ [Invoice下载] 第一次进入generating_bills状态，触发查询')
              queryData()
              hasQueriedOnce = true
              console.log('[Invoice下载] hasQueriedOnce 设置为:', hasQueriedOnce)
            } else if (task.status === 'generating_bills' && hasQueriedOnce) {
              console.log('⏭️  [Invoice下载] generating_bills状态，但已查询过，跳过')
            } else if (task.status === 'completed') {
              // 任务完成时停止轮询
              clearInterval(progressTimer)
              progressTimer = null
              ElMessage.success('Invoice同步完成')
              downloading.value = false
              
              // 如果之前没有查询过（直接从running跳到completed），则查询一次
              if (!hasQueriedOnce) {
                console.log('✅ [Invoice下载] 任务完成且未查询过，触发查询')
                queryData()
              } else {
                console.log('⏭️  [Invoice下载] 任务完成但已查询过，不再查询')
              }
            } else if (task.status === 'failed') {
              clearInterval(progressTimer)
              progressTimer = null
              ElMessage.error('Invoice同步失败')
              downloading.value = false
            } else {
              console.log('⏸️  [Invoice下载] 状态:', task.status, '- 仅更新进度，不查询')
            }
          } else if (data.code === 404) {
            // 任务不存在，停止轮询
            console.warn('[Invoice下载] 任务不存在，停止轮询')
            clearInterval(progressTimer)
            progressTimer = null
            ElMessage.error('下载任务不存在，可能已过期')
            downloading.value = false
          }
        } catch (error) {
          console.error('[Invoice下载] 查询进度失败:', error)
        }
      }, 2000) // 每2秒查询一次
    }
    
    // 更新表格中的下载进度
    const updateDownloadProgress = (task) => {
      console.log('[Invoice下载] 更新进度:', task)
      
      for (const agent of monitorData.value) {
        const payerNodes = childrenMap.value[agent.id]
        if (payerNodes) {
          payerNodes.forEach(payer => {
            if (payer.payer_id && task.payers[payer.payer_id]) {
              console.log(`[Invoice下载] 更新Payer ${payer.payer_id}:`, task.payers[payer.payer_id])
              payer.invoice_download_progress = {
                downloaded: task.payers[payer.payer_id].downloaded,
                total: task.payers[payer.payer_id].total,
                status: task.payers[payer.payer_id].status
              }
            }
          })
        }
      }
    }
    
    const showUploadDialog = () => {
      uploadDialogVisible.value = true
    }
    
    // 显示配置对话框
    const showConfigDialog = () => {
      configDialogVisible.value = true
    }
    
    // 重新生成
    const regenerate = async (row) => {
      try {
        await ElMessageBox.confirm(
          `将依次生成：\n1. CUR账单\n2. CUR对账单\n3. Invoice版本账单和对账单\n\n确定要重新生成 Link ${row.link_id} 的所有文件吗？`,
          '确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
            dangerouslyUseHTMLString: true
          }
        )
        
        loading.value = true
        
        // 1. 重新下载原始账单（会自动生成CUR账单和对账单）
        try {
          ElMessage.info('正在重新下载原始账单并生成CUR账单和对账单，请耐心等待...')
          const params = new URLSearchParams({
            month: filters.month,
            payerId: row.payer_id,
            linkId: row.link_id
          })
          console.log('[Step 1] Calling reDownload with params:', { month: filters.month, payerId: row.payer_id, linkId: row.link_id })
          
          const reDownloadResponse = await fetch(`/api/bill/reDownload/?${params}`, {
            method: 'GET'
          })
          
          console.log('[Step 1] Response status:', reDownloadResponse.status)
          const reDownloadData = await reDownloadResponse.json()
          console.log('[Step 1] Response data:', reDownloadData)
          
          if (reDownloadData.code !== 2000 && reDownloadData.code !== 200) {
            throw new Error(`CUR账单和对账单生成失败: ${reDownloadData.msg || '未知错误'}`)
          }
          ElMessage.success('CUR账单和对账单生成成功')
        } catch (error) {
          console.error('[Step 1] Error:', error)
          ElMessage.error(error.message || 'CUR账单和对账单生成失败')
          loading.value = false
          return
        }
        
        // 2. 生成Invoice版本的账单和对账单
        try {
          ElMessage.info('正在生成Invoice版本账单和对账单...')
          const invoiceResponse = await fetch('/api/bill/invoice-monitor/regenerate/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              link_id: row.link_id,
              payer_id: row.payer_id,
              month: filters.month
            })
          })
          
          const invoiceData = await invoiceResponse.json()
          if (invoiceData.code === 200) {
            ElMessage.success('所有文件生成成功！')
            setTimeout(() => {
              queryData()
            }, 30000)
          } else {
            throw new Error(invoiceData.msg || 'Invoice版本生成失败')
          }
        } catch (error) {
          ElMessage.error(error.message || 'Invoice版本生成失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('重新生成失败:', error)
          ElMessage.error('重新生成失败，请重试')
        }
      } finally {
        loading.value = false
      }
    }
    
    // 上传成功回调
    const handleUploadSuccess = () => {
      uploadDialogVisible.value = false
      queryData()
    }
    
    // 弹窗关闭回调
    const handleUploadDialogClose = () => {
      if (uploadContentRef.value && uploadContentRef.value.resetForm) {
        uploadContentRef.value.resetForm()
      }
    }
    
    // 配置成功回调
    const handleConfigSuccess = () => {
      configDialogVisible.value = false
    }
    
    // 生成全部对账单
    const generateAllReconciliation = async () => {
      if (!filters.month || !filters.sales) {
        ElMessage.warning('请选择月份和销售')
        return
      }
      
      try {
        await ElMessageBox.confirm(
          '将根据当前筛选条件批量生成所有符合条件的Invoice版本伙伴对账单和客户对账单，确定继续吗？',
          '确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        loading.value = true
        ElMessage.info('正在批量生成对账单，请耐心等待...')
        
        const params = {
          month: filters.month,
          sales: filters.sales,
          billType: 2,
          batch_generate: 'true'
        }
        
        if (filters.agent_id) params.agent_id = filters.agent_id
        if (filters.payer_id) params.payer_id = filters.payer_id
        
        const data = await downloadBillDetail(params)
        
        if (data.code === 2000 || data.code === 200) {
          ElMessage.success(data.msg || '对账单生成任务已提交，请稍后查看进度')
          setTimeout(() => {
            queryData()
          }, 3000)
        } else {
          ElMessage.error(data.msg || '生成失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('生成全部对账单失败:', error)
          ElMessage.error('生成失败，请重试')
        }
      } finally {
        loading.value = false
      }
    }
    
    const curIntegrityColor = (code) => {
      if (code === 'missing') return '#f56c6c'
      if (code === 'partial') return '#e6a23c'
      return '#67c23a'
    }
    const curIntegrityIcon = (code) => {
      if (code === 'missing') return '❌'
      if (code === 'partial') return '⚠️'
      return '✅'
    }

    return {
      loading,
      uploadDialogVisible,
      configDialogVisible,
      uploadContentRef,
      filters,
      filterZeroData,
      salesList,
      agentList,
      payersList,
      linksList,
      displayedLinksList,
      linkFilterMethod,
      monitorData,
      monitorTableRef,
      tableKey,
      isAdmin,
      loadChildren,
      queryData,
      resetFilters,
      showUploadDialog,
      startDownloadDirect,
      downloading,
      showConfigDialog,
      regenerate,
      handleUploadSuccess,
      handleUploadDialogClose,
      handleConfigSuccess,
      generateAllReconciliation,
      curIntegrityColor,
      curIntegrityIcon
    }
  }
}
</script>

<style lang="scss" scoped>
.invoice-monitor {
  padding: 20px;
  
  .filter-section {
    background: #fff;
    padding: 20px 24px;
    border-radius: 12px;
    margin-bottom: 16px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  }

  .filter-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 16px 20px;
    align-items: end;
    max-width: 85%;
  }

  .filter-actions {
    grid-column: 1 / -1;
    display: flex;
    align-items: flex-end;
    gap: 10px;
    padding-top: 4px;
    border-top: 1px solid #f3f4f6;
  }

  .filter-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .filter-label {
    font-size: 11px;
    color: #6b7280;
    font-weight: 500;
    white-space: nowrap;
    line-height: 1;
  }

  .filter-input {
    :deep(.el-input__wrapper) {
      height: 34px;
      border-radius: 8px;
      box-shadow: none;
      background: #f3f4f6;
      border: none;
      &:hover { background: #e9eaec; }
      &.is-focus { box-shadow: 0 0 0 2px #1D6FE8 inset; background: #fff; }
    }
    :deep(.el-input__inner) {
      font-size: 13px;
    }
  }

  .action-btn {
    height: 34px;
    padding: 0 14px;
    font-size: 13px;
    border-radius: 8px;
    background: #fff;
    border: 1px solid #e5e7eb;
    color: #1a1a2e;
    white-space: nowrap;
    &:hover { background: #f3f4f6; border-color: #9ca3af; }

    &--dark {
      background: #1a1a2e;
      border-color: #1a1a2e;
      color: #fff;
      &:hover { background: #374151; border-color: #374151; }
    }

    &--primary {
      background: #1a1a2e;
      border-color: #1a1a2e;
      color: #fff;
      &:hover { background: #374151; border-color: #374151; }
    }
  }

  .filter-checkbox {
    height: 34px;
    display: flex;
    align-items: center;
    white-space: nowrap;
    font-size: 13px;
  }
  
  .monitor-table {
    background: #fff;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
    
    :deep(.el-table) {
      border-bottom: 1px solid #e8eaec;
    }
  }
}
</style>
